// Chilean SII document-type metadata for the Libro de Compras (F29).
//
// Maps the AP internal `doc_tipo_code` to its SII numeric code (the "Cod. SII" column) and the
// Spanish section header Softland prints, and fixes the order the sections appear in the ledger.
// Single source for the Libro de Compras grouping.
//
// Internal codes come from the BSale webhook (`bsale-thirdparty-webhook`): FE=33, FNE=34, NC=61,
// ND=56, FC=46. DI=914 is the DIN (Declaración de Ingreso de importación).

export interface SiiDocTypeMeta {
  /** Internal AP doc_tipo_code. */
  code: string;
  /** SII document code shown in the "Cod. SII" column. */
  siiCode: number;
  /** Section header Softland prints above the group. */
  sectionTitle: string;
}

// Order follows the Softland "Libro de Compras": NC, ND, DIN, factura afecta, factura exenta, y
// factura de compra. Secciones sin documentos en el período simplemente no se imprimen.
export const LIBRO_COMPRAS_DOC_TYPES: SiiDocTypeMeta[] = [
  {
    code: "NC",
    siiCode: 61,
    sectionTitle: "NOTA DE CREDITO ELECTRONICA (NOTA CREDITO ELECTRONICA COMPRAS)",
  },
  {
    code: "ND",
    siiCode: 56,
    sectionTitle: "NOTA DE DEBITO ELECTRONICA (NOTA DEBITO ELECTRONICA COMPRAS)",
  },
  {
    code: "DI",
    siiCode: 914,
    sectionTitle: "Declaracion de Ingreso (DIN) (DECLARACION DE INGRESO)",
  },
  {
    code: "FE",
    siiCode: 33,
    sectionTitle: "FACTURA ELECTRONICA (FACTURA AFECTA ELECTRONICA COMPRAS)",
  },
  {
    code: "FNE",
    siiCode: 34,
    sectionTitle: "FACTURA NO AFECTA O EXENTA ELECTRONICA (FACTURA EXENTA ELECTRONICA COMPRAS)",
  },
  {
    code: "FC",
    siiCode: 46,
    sectionTitle: "FACTURA DE COMPRA ELECTRONICA (FACTURA DE COMPRA ELECTRONICA COMPRAS)",
  },
];

// Facturas (FEX) y notas de crédito (NCEX) de proveedores extranjeros (p. ej. Bunge Argentina,
// Transporte Barbería) NO entran al Libro de Compras: la importación ya se declara vía la DIN (914),
// así que incluir además el documento extranjero duplicaría el monto. Son exentos y no generan IVA.
// (Confirmado con contabilidad, octubre 2026.)
export const LIBRO_COMPRAS_EXCLUDED_CODES = ["FEX", "NCEX"];

// Nota de crédito de proveedor (reduce lo que debemos): la NC electrónica nacional (SII 61) y la nota
// de crédito extranjera (NCEX, sin código SII, excluida del libro). En el sub-mayor de CxP ambas se
// guardan con monto positivo ("en el haber") y se cruzan contra la deuda del mismo proveedor.
export function isApCreditNote(code: string | null | undefined): boolean {
  return code === "NC" || code === "NCEX";
}

const META_BY_CODE = new Map(LIBRO_COMPRAS_DOC_TYPES.map((m) => [m.code, m]));

/** Section metadata for an internal doc_tipo_code, or null if it isn't a known libro type. */
export function siiDocTypeMeta(code: string): SiiDocTypeMeta | null {
  return META_BY_CODE.get(code) ?? null;
}
